/**
 * Monitoring Enums
 *
 * Defines all enumerated types used throughout the monitoring infrastructure.
 * These enums provide type safety and consistency across the application.
 *
 * Focus: Operational monitoring for Datadog RUM (performance, errors, system health)
 * Behavioral analytics continue to use Segment for product insights.
 */

/**
 * Event categorization for routing to appropriate monitoring systems
 */
export enum EventCategory {
  /** Operational events: performance, errors, system health (→ Datadog) */
  OPERATIONAL = 'operational',
  /** Behavioral events: user actions, feature usage (→ Segment) */
  BEHAVIORAL = 'behavioral',
  /** Critical events that need both operational and product visibility (→ Both) */
  CRITICAL = 'critical',
}

/**
 * Hierarchical action types for user interaction tracking
 */
export enum ActionType {
  /** Complete user journeys spanning multiple interactions */
  WORKFLOW = 'workflow',
  /** User-initiated feature operations and commands */
  ACTION = 'action',
  /** Low-level UI events and system operations */
  EVENT = 'event',
}

/**
 * Action execution status tracking
 */
export enum ActionStatus {
  /** Action has been initiated */
  STARTED = 'started',
  /** Action completed successfully */
  SUCCESS = 'success',
  /** Action failed with error */
  FAILURE = 'failure',
  /** Action was cancelled by user or system */
  CANCELLED = 'cancelled',
}

/**
 * Workflow event types (base names without suffixes)
 */
export enum WorkflowType {
  UPLOAD_STORY = 'upload_story',
  VIDEO_LOADING = 'video_loading', // Video loading workflow from loadVideo/createNewVideoFromSource through headless editor integration to first save
  // VIDEO_EDITING = 'video_editing', // Complete video editing session from open to save
  // VIDEO_PUBLISHING = 'video_publishing', // Video publishing and rendering workflow
  // CONTENT_SHARING = 'content_sharing', // Content sharing and distribution workflow
  // TRANSCRIPT_EDITING = 'transcript_editing', // Transcript editing and correction workflow
  // USER_ONBOARDING = 'user_onboarding', // User onboarding and setup workflow
  // CONTENT_MANAGEMENT = 'content_management', // Dashboard and content management workflow
  // SETTINGS_CONFIGURATION = 'settings_configuration', // Settings and configuration workflow
}

/**
 * Action event types (base names without suffixes)
 */
export enum ActionEventType {
  CLIP_PRODUCTION = 'clip_production',

  // === LEGACY EVENTS (kept as-is for backward compatibility) ===
  FIX_TRANSCRIPTION = 'fix_transcription',
  CLIENT_RENDERING_ERROR = 'client_rendering_error',
  VIDEO_PUBLISH = 'video_publish',
  SHARING_CONNECT_EXTERNAL = 'sharing_connect_external',
  SHAREABLE_IMAGE_DOWNLOAD = 'shareable_image_download',
  COPY_TRANSCRIPTION = 'copy_transcription',
  COPY_TALKING_POINTS = 'copy_talking_points',
  PRODUCE_CLIPS = 'produce_clips',
  KARAOKE_TEXT_GENERATED = 'karaoke_text_generated',
  TRANSCRIPT_EDITOR_OPENED = 'transcript_editor_opened',
  TRANSCRIPT_EDITOR_CLOSED = 'transcript_editor_closed',
  TRANSCRIPT_COPIED = 'transcript_copied',
  TRANSCRIPT_SELECTION_ALIGNMENT = 'transcript_selection_alignment',
  VIDEO_PUBLISHED = 'video_published',
  DOWNLOAD_CLIP = 'download_clip',
  CREATE_SHAPE_ELEMENT = 'create_shape_element',

  // === STORY UPLOAD (new granular instrumentation) ===
  // #phase:P3 - renderer / workflow monitoring enhancement
  STORY_SOURCE_SELECT = 'story_source_select',
  STORY_VIDEO_UPLOAD = 'story_video_upload',
  STORY_TYPE_SELECT = 'story_type_select',
  STORY_METADATA_SUBMIT = 'story_metadata_submit',
  STORY_ARTIFACT_UPLOAD = 'story_artifact_upload',
  STORY_BROLL_UPLOAD = 'story_broll_upload',
  STORY_PROCESSING = 'story_processing',

  // === VIDEO LOADING WORKFLOW ===
  SETUP_HEADLESS_INTEGRATION = 'setup_headless_integration',
  LOAD_TRANSCRIPT = 'load_transcript',
  LOAD_KARAOKE_DATA = 'load_karaoke_data',
  TRANSCRIPT_SEGMENT_ARCHITECTURE_MIGRATION = 'transcript_segment_architecture_migration',
  RENDERER_SET_SOURCE = 'renderer_set_source',
}

/**
 * Event event types (base names without suffixes)
 * Currently empty as we don't have event-level monitoring yet
 */
export enum EventEventType {}
// Future event-level monitoring will include:
// - UI interactions, clicks, hovers
// - Low-level system events
// - Performance measurements

/**
 * Device types for technical context
 */
export enum DeviceType {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
  TABLET = 'tablet',
}

/**
 * Log levels for monitoring events
 */
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

/**
 * Sampling strategies for different action types
 */
export enum SamplingStrategy {
  /** Always sample - for critical business metrics */
  ALWAYS = 'always',
  /** High sampling rate - for important features */
  HIGH = 'high',
  /** Medium sampling rate - for regular features */
  MEDIUM = 'medium',
  /** Low sampling rate - for high-frequency events */
  LOW = 'low',
  /** Never sample - for disabled features */
  NEVER = 'never',
}

/**
 * Event category mapping for routing events to appropriate monitoring systems
 * Maps base event names to categories
 */
export const EVENT_CATEGORY_MAP: Record<string, EventCategory> = {
  // === WORKFLOW EVENTS ===
  [WorkflowType.UPLOAD_STORY]: EventCategory.CRITICAL,
  [WorkflowType.VIDEO_LOADING]: EventCategory.OPERATIONAL,

  // === VIDEO LOADING WORKFLOW SUB-ACTIONS ===
  [ActionEventType.SETUP_HEADLESS_INTEGRATION]: EventCategory.OPERATIONAL,
  [ActionEventType.LOAD_TRANSCRIPT]: EventCategory.OPERATIONAL,
  [ActionEventType.LOAD_KARAOKE_DATA]: EventCategory.OPERATIONAL,
  [ActionEventType.TRANSCRIPT_SEGMENT_ARCHITECTURE_MIGRATION]:
    EventCategory.OPERATIONAL,
  [ActionEventType.RENDERER_SET_SOURCE]: EventCategory.OPERATIONAL,

  // === ACTION EVENTS ===
  [ActionEventType.CLIP_PRODUCTION]: EventCategory.OPERATIONAL,

  // === LEGACY ACTION EVENTS (kept as-is for backward compatibility) ===
  [ActionEventType.FIX_TRANSCRIPTION]: EventCategory.BEHAVIORAL,
  [ActionEventType.CLIENT_RENDERING_ERROR]: EventCategory.OPERATIONAL,
  [ActionEventType.VIDEO_PUBLISH]: EventCategory.BEHAVIORAL,
  [ActionEventType.SHARING_CONNECT_EXTERNAL]: EventCategory.BEHAVIORAL,
  [ActionEventType.SHAREABLE_IMAGE_DOWNLOAD]: EventCategory.BEHAVIORAL,
  [ActionEventType.COPY_TRANSCRIPTION]: EventCategory.BEHAVIORAL,
  [ActionEventType.COPY_TALKING_POINTS]: EventCategory.BEHAVIORAL,
  [ActionEventType.PRODUCE_CLIPS]: EventCategory.BEHAVIORAL,
  [ActionEventType.KARAOKE_TEXT_GENERATED]: EventCategory.BEHAVIORAL,
  [ActionEventType.TRANSCRIPT_EDITOR_OPENED]: EventCategory.BEHAVIORAL,
  [ActionEventType.TRANSCRIPT_EDITOR_CLOSED]: EventCategory.BEHAVIORAL,
  [ActionEventType.TRANSCRIPT_COPIED]: EventCategory.BEHAVIORAL,
  [ActionEventType.TRANSCRIPT_SELECTION_ALIGNMENT]: EventCategory.BEHAVIORAL,
  [ActionEventType.VIDEO_PUBLISHED]: EventCategory.BEHAVIORAL,
  [ActionEventType.DOWNLOAD_CLIP]: EventCategory.BEHAVIORAL,
  [ActionEventType.CREATE_SHAPE_ELEMENT]: EventCategory.BEHAVIORAL,

  // === STORY UPLOAD (granular) ===
  [ActionEventType.STORY_SOURCE_SELECT]: EventCategory.BEHAVIORAL,
  [ActionEventType.STORY_VIDEO_UPLOAD]: EventCategory.OPERATIONAL,
  [ActionEventType.STORY_TYPE_SELECT]: EventCategory.BEHAVIORAL,
  [ActionEventType.STORY_METADATA_SUBMIT]: EventCategory.BEHAVIORAL,
  [ActionEventType.STORY_ARTIFACT_UPLOAD]: EventCategory.OPERATIONAL,
  [ActionEventType.STORY_BROLL_UPLOAD]: EventCategory.OPERATIONAL,
  [ActionEventType.STORY_PROCESSING]: EventCategory.CRITICAL,
};
